import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Navbar } from "@/components/Navbar";
import { Lock, Mail, User, ArrowRight, Gift } from "lucide-react";
import { trackEvent } from "@/lib/track";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function MemberSignupPage() {
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [loading, setLoading] = useState(false);
  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || form.password.length < 6) {
      toast.error("Please enter your name, email and a password (6+ characters).");
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/member/signup`, form);
      localStorage.setItem("member_token", res.data.token);
      localStorage.setItem("member_name", res.data.member.name);
      trackEvent("sign_up", { content_name: "Member Signup" });
      toast.success(`Welcome, ${res.data.member.name.split(" ")[0]}! Your account is ready.`);
      navigate("/member/dashboard");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Signup failed. Try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white dark:bg-[#0f1117]" data-testid="member-signup-page">
      <Navbar />
      <section className="hero-gradient dark:bg-transparent pt-32 pb-16 px-6">
        <div className="max-w-md mx-auto animate-fade-in-up">
          <div className="text-center mb-8">
            <Badge className="bg-brand-gold text-white mb-4 font-body font-semibold px-4 py-1.5 rounded-full">
              <Gift className="inline h-3.5 w-3.5 mr-1" /> Free account — talk to us to get started
            </Badge>
            <h1 className="font-heading text-3xl md:text-4xl font-bold text-slate-900 dark:text-white mb-3">Create your account</h1>
            <p className="text-slate-600 dark:text-slate-400 font-body">Sign up now — then buy credits for the subject you need, or we'll assign them after your enrolment call.</p>
          </div>

          <form onSubmit={handleSubmit} className="bg-white dark:bg-[#1a1b23] rounded-2xl border border-slate-100 dark:border-slate-800 p-8 shadow-[0_8px_30px_rgb(0,0,0,0.04)] space-y-5" data-testid="member-signup-form">
            <div>
              <Label className="font-body text-sm text-slate-700 dark:text-slate-200 mb-1.5 block">Full name</Label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input value={form.name} onChange={update("name")} required autoComplete="name"
                  className="pl-10 rounded-xl font-body" placeholder="Your name" data-testid="signup-input-name" />
              </div>
            </div>
            <div>
              <Label className="font-body text-sm text-slate-700 dark:text-slate-200 mb-1.5 block">Email</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input type="email" value={form.email} onChange={update("email")} required autoComplete="email"
                  className="pl-10 rounded-xl font-body" placeholder="you@example.com" data-testid="signup-input-email" />
              </div>
            </div>
            <div>
              <Label className="font-body text-sm text-slate-700 dark:text-slate-200 mb-1.5 block">Password</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input type="password" value={form.password} onChange={update("password")} required autoComplete="new-password"
                  className="pl-10 rounded-xl font-body" placeholder="At least 6 characters" data-testid="signup-input-password" />
              </div>
            </div>

            <Button type="submit" disabled={loading}
              className="w-full rounded-full bg-brand-gold hover:bg-brand-gold-hover text-white font-body font-bold py-6"
              data-testid="signup-submit-btn">
              {loading ? "Creating account..." : <>Create account <ArrowRight className="ml-2 h-4 w-4" /></>}
            </Button>

            <div className="pt-3 border-t border-slate-100 dark:border-slate-800 text-center">
              <p className="text-xs text-slate-500 dark:text-slate-400 font-body">
                Already a member?{" "}
                <Link to="/member/login" className="text-brand-purple font-semibold hover:underline" data-testid="signup-login-link">
                  Sign in
                </Link>
              </p>
            </div>
          </form>

          <p className="text-xs text-slate-400 dark:text-slate-500 font-body text-center mt-4">
            Need more credits? Browse our <Link to="/pricing" className="text-brand-purple hover:underline font-semibold">pricing plans</Link>.
          </p>
        </div>
      </section>
    </div>
  );
}
